import type { DocumentOut, ExtractionOut } from "@/api";

/**
 * „Andere Seite wählen“ (Seitenwahl, Folgepunkt a — 13.10.2026).
 *
 * A reading of one page that failed, or whose outline was not sure, may
 * simply have read the wrong page. Where the server names the original
 * behind the sheet it read (`source.originalDocumentId`), a person can choose
 * another page of it — the same raster as before the first reading, on the
 * reading's own route: `/einlesen?seiten=1`. The URL is the state, as with
 * `?lesarten=1` on the schema route: a reload keeps the choice open.
 */
export const OTHER_PAGE_PARAM = "seiten";

export type ExtractionSource = ExtractionOut["source"];

/** Why another page is offered: the reading failed, or its outline was not sure. */
export type OtherPageOutcome = "failed" | "unsure";

export function otherPageHref(caseId: string): string {
  return `/vorgang/${caseId}/einlesen?${OTHER_PAGE_PARAM}=1`;
}

/**
 * Whether this reading may be followed by another page of its original —
 * and why. Only a page sheet has an original; a reading that is running or
 * waiting is not done with its page yet, and one whose outline is drawn
 * (checked by the machine, or chosen by a person) read the right page.
 * Whether a Stand exists is the caller's question: then nothing is read.
 */
export function otherPageOutcome(job: ExtractionOut | undefined): OtherPageOutcome | null {
  if (job === undefined || job.source.originalDocumentId === null) return null;
  if (job.status === "failed") return "failed";
  const form = job.status === "succeeded" ? job.review?.formStatus : undefined;
  return form === "mehrdeutig" || form === "offen" ? "unsure" : null;
}

/** The original behind the sheet, as the Vorgang lists it — `null` where it is not (or no longer) on file. */
export function originalOf(source: ExtractionSource, documents: readonly DocumentOut[] | undefined): DocumentOut | null {
  const id = source.originalDocumentId;
  return id === null ? null : ((documents ?? []).find((document) => document.id === id) ?? null);
}

/**
 * „Seite 2 aus „Angebot.pdf““ — what a reading of a page sheet read; „Seite 2“
 * where the original is not on file; `null` for a document read as it is.
 * The file name in „…“, as the timeline writes it.
 */
export function readFromLabel(source: ExtractionSource, original: DocumentOut | null): string | null {
  if (source.originalPage === null) return null;
  const page = `Seite ${source.originalPage}`;
  return original !== null ? `${page} aus „${original.originalFilename}“` : page;
}
